import { NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'

import { AutoCompleteModule } from 'primeng/autocomplete'
import { PaginatorModule } from 'primeng/paginator'

import { Subject, of } from 'rxjs'
import { catchError, debounceTime, map, startWith } from 'rxjs/operators'

import {
  FlxButtonComponent,
  FlxErrorComponent,
  FlxIconComponent,
  FlxInfoHelperComponent,
  FlxInputComponent,
  FlxInputDirective,
  FlxLabelComponent,
  FlxSelectComponent,
} from '@flowx/angular-ui-toolkit'
import {
  UserForm,
  UserFormAttribute,
  formAttributesFromApiAttributes,
} from '@flowx/designer/modules/access-management/access-management.utils'
import {
  AccessManagementAttributes,
  AccessManagementRole,
  UserGroup,
} from '@flowx/designer/modules/core/dictionary/access-management.dictionary'
import { ALL_END_USERS } from '@flowx/designer/modules/workspaces/shared/commons.types'
import { OrganizationAdminGroupsApiService } from '@flowx/designer/shared/api/organization-admin-groups.api'
import { RoleApiService } from '@flowx/designer/shared/api/role.api'
import { FlxAutofocusDirective } from '@flowx/designer/shared/directives/autofocus.directive'
import { AlphanumericUnderlineRegex, UsersType } from '@flowx/designer/shared/entities/utils'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { ACCESS_MANAGEMENT_GROUPS_API } from '../../workspaces'

@Component({
  selector: 'flx-user-runtime-options',
  templateUrl: 'user-runtime-options.component.html',
  styleUrl: 'user-runtime-options.component.scss',
  imports: [
    FlxLabelComponent,
    FlxSelectComponent,
    PaginatorModule,
    ReactiveFormsModule,
    FlxButtonComponent,
    NgTemplateOutlet,
    AutoCompleteModule,
    FlxAutofocusDirective,
    FlxErrorComponent,
    FlxIconComponent,
    FlxInputComponent,
    FlxInputDirective,
    FlxInfoHelperComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: ACCESS_MANAGEMENT_GROUPS_API,
      useClass: OrganizationAdminGroupsApiService,
    },
  ],
})
export class UserRuntimeOptionsComponent {
  #roleApiService = inject(RoleApiService)
  #groupApiService = inject(ACCESS_MANAGEMENT_GROUPS_API)
  #formBuilder = inject(FormBuilder)
  #toastMessageService = inject(ToastMessageService)

  @ViewChild('attributesSection', { static: false }) attributesSection: ElementRef

  userModalFG = input.required<FormGroup<UserForm<AccessManagementRole, UserGroup>>>()
  enableErrorValidation = input.required<boolean>()
  canEdit = input<boolean>(true)
  persistedAttributes = input<AccessManagementAttributes>()
  rolesVisible = input<boolean>(true)
  groupsVisible = input<boolean>(true)
  attributesVisible = input<boolean>(true)
  attributesDisabled = input<boolean>(false)
  isSelfEdit = input<boolean>(false)
  usersType = input.required<UsersType>()

  rolesIsLoading = output<boolean>()
  groupsIsLoading = output<boolean>()

  roles = rxResource({
    params: () => this.rolesVisible() || undefined,
    stream: () =>
      this.#roleApiService.getRoles().pipe(
        map((roles) =>
          roles.map((role) => ({
            value: role,
            label: role.name,
            enabled: true,
          }))
        ),
        catchError(() => {
          this.#toastMessageService.showErrorMessage('Failed to load Runtime Roles.')
          return of([])
        })
      ),
    defaultValue: [],
  })

  rolesFilter = signal('')

  filteredRoles = computed(() => {
    const roles = this.roles.value()
    const filter = this.rolesFilter().toLowerCase()

    if (!filter) {
      return roles
    }

    return roles.filter((role) => role.value.name.toLowerCase().includes(filter))
  })

  filteredGroups = rxResource({
    params: () => {
      const groupsVisible = this.groupsVisible()
      const filterKey = this.groupsFilter()
      const groupsFormControlDisabled = this.userModalFG().controls.groups.disabled
      if (!groupsVisible || groupsFormControlDisabled) {
        return undefined
      }
      return { filterKey }
    },
    stream: ({ params }) =>
      this.#groupApiService.getAllGroups(params.filterKey).pipe(
        map((groups) =>
          groups.map((group) => ({
            value: group,
            label: group.name,
            enabled: !this.userModalFG().controls.groups.value?.find(
              (selectedGroup) => selectedGroup.value.id === group.id
            ),
          }))
        ),
        catchError(() => {
          this.#toastMessageService.showErrorMessage('Failed to load Runtime Groups.')
          return of([])
        })
      ),
    defaultValue: [],
  })

  groupsFilter$ = new Subject<string>()
  groupsFilter = toSignal(this.groupsFilter$.pipe(startWith(''), debounceTime(300)))

  groupOptions = computed(() =>
    this.filteredGroups.value().filter((group) => group.value.name !== ALL_END_USERS)
  )

  groupsLabel = computed(() => {
    const isSelfEdit = this.isSelfEdit()
    const usersType = this.usersType()
    return isSelfEdit && usersType !== UsersType.RUNTIME ? 'End-Users Groups' : 'Groups Membership'
  })

  constructor() {
    effect(() => {
      const persistedAttributes = this.persistedAttributes()

      if (persistedAttributes) {
        const attributes = this.#formBuilder.array(
          formAttributesFromApiAttributes(persistedAttributes).map((attribute) =>
            this.getAttributeFormGroup(attribute)
          ),
          [this.uniqueKeysValidator()]
        )

        // Re-created array must inherit the disabled state set on the parent form.
        if (this.attributesDisabled()) {
          attributes.disable()
        }

        this.userModalFG().setControl('attributes', attributes)
      } else {
        this.userModalFG().controls.attributes?.addValidators([this.uniqueKeysValidator()])
      }
    })

    const rolesIsLoadingEffect = effect(() => {
      if (this.rolesVisible()) {
        this.rolesIsLoading.emit(this.roles.isLoading())
      } else {
        rolesIsLoadingEffect.destroy()
      }
    })

    const groupsIsLoadingEffect = effect(() => {
      if (this.groupsVisible()) {
        this.groupsIsLoading.emit(this.filteredGroups.isLoading())
      } else {
        groupsIsLoadingEffect.destroy()
      }
    })

    effect(() => {
      const groups = this.filteredGroups.value()
      const allEndUsersGroup = groups.find((group) => group.value.name === ALL_END_USERS)
      const selectedGroupFC = this.userModalFG().controls.groups
      if (this.groupsVisible() && !selectedGroupFC.value?.length) {
        selectedGroupFC.patchValue(
          allEndUsersGroup ? [{ ...allEndUsersGroup, enabled: false }] : [],
          { emitEvent: false }
        )
      }
    })
  }

  uniqueKeysValidator() {
    return (control: FormArray): null => {
      const keyControlPairs = control.controls
        .filter((childFG: FormGroup) => childFG.value.key)
        .map((childFG: FormGroup) => ({
          key: childFG.value.key,
          formControl: childFG.controls.key,
        }))

      keyControlPairs.forEach((keyControlPair, index) => {
        if (keyControlPair.formControl.hasError('duplicateKey')) {
          const errors = keyControlPair.formControl.errors
          delete errors.duplicateKey

          if (Object.keys(errors).length) {
            keyControlPair.formControl.setErrors(errors)
          } else {
            keyControlPair.formControl.setErrors(null)
          }
        }

        keyControlPairs.forEach((comparisonKeyControlPair, comparisonIndex) => {
          if (index !== comparisonIndex && keyControlPair.key === comparisonKeyControlPair.key) {
            keyControlPair.formControl.setErrors({ duplicateKey: true })
            comparisonKeyControlPair.formControl.setErrors({ duplicateKey: true })
            keyControlPair.formControl.markAsTouched()
            comparisonKeyControlPair.formControl.markAsTouched()
          }
        })
      })

      return null
    }
  }

  get attributesFA(): FormArray<AbstractControl<UserFormAttribute>> {
    return this.userModalFG().get('attributes') as FormArray<AbstractControl<UserFormAttribute>>
  }

  addAttribute(): void {
    this.attributesFA.push(this.getAttributeFormGroup())

    setTimeout(() => {
      this.attributesSection.nativeElement.scrollTo(
        0,
        this.attributesSection.nativeElement.scrollHeight
      )
    }, 0)
  }

  deleteAttribute(index: number): void {
    this.attributesFA.removeAt(index)
  }

  private getAttributeFormGroup(attribute?: UserFormAttribute): AbstractControl {
    return this.#formBuilder.group({
      key: [
        { value: attribute?.key ?? '', disabled: !this.canEdit() },
        [Validators.required, Validators.pattern(AlphanumericUnderlineRegex)],
      ],
      values: [
        { value: attribute?.values ?? [], disabled: !this.canEdit() },
        [Validators.required],
      ],
    })
  }
}
